import { type FormEvent, type ReactElement, useState } from 'react';
import { Button, CheckboxField, FormField } from '@/shared/ui';
import { useSignUp } from '../model/use-sign-up';
import { SUBMIT_BUTTON_CLASS, UNDERLINE_INPUT_CLASS } from './auth-form-styles';
import { SubmitArrow } from './submit-arrow';

export interface SignUpFormProps {
  readonly onSuccess?: () => void;
}

export const SignUpForm = ({ onSuccess }: SignUpFormProps): ReactElement => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [termsOfService, setTermsOfService] = useState(false);
  const [privacyPolicy, setPrivacyPolicy] = useState(false);
  const [ageOver19, setAgeOver19] = useState(false);
  const [marketingEmail, setMarketingEmail] = useState(false);
  const [marketingSms, setMarketingSms] = useState(false);
  const { fieldErrors, agreementErrors, formError, isPending, submit } = useSignUp();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    // 필수 3종의 미체크는 스키마(true 리터럴)가 항목별 오류로 돌려준다.
    const succeeded = await submit({
      email,
      password,
      name,
      agreements: { termsOfService, privacyPolicy, ageOver19, marketingEmail, marketingSms },
    });

    if (succeeded) {
      onSuccess?.();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6" noValidate>
      <FormField
        label="이메일"
        type="email"
        autoComplete="email"
        isRequired
        className={UNDERLINE_INPUT_CLASS}
        value={email}
        error={fieldErrors.email}
        onChange={(event) => setEmail(event.target.value)}
      />

      <FormField
        label="비밀번호"
        type="password"
        autoComplete="new-password"
        description="10자 이상, 대문자 · 소문자 · 숫자를 포함해야 합니다."
        isRequired
        className={UNDERLINE_INPUT_CLASS}
        value={password}
        error={fieldErrors.password}
        onChange={(event) => setPassword(event.target.value)}
      />

      <FormField
        label="이름"
        autoComplete="name"
        isRequired
        className={UNDERLINE_INPUT_CLASS}
        value={name}
        error={fieldErrors.name}
        onChange={(event) => setName(event.target.value)}
      />

      <fieldset className="flex flex-col gap-3 rounded-[1.25rem] bg-muted/60 p-5">
        <legend className="float-left mb-1 text-sm font-semibold">약관 동의</legend>

        <CheckboxField
          label="이용약관 동의 (필수)"
          checked={termsOfService}
          error={agreementErrors.termsOfService}
          onChange={(event) => setTermsOfService(event.target.checked)}
        />
        <CheckboxField
          label="개인정보 처리방침 동의 (필수)"
          checked={privacyPolicy}
          error={agreementErrors.privacyPolicy}
          onChange={(event) => setPrivacyPolicy(event.target.checked)}
        />
        <CheckboxField
          label="만 19세 이상입니다 (필수)"
          checked={ageOver19}
          error={agreementErrors.ageOver19}
          onChange={(event) => setAgeOver19(event.target.checked)}
        />
        <CheckboxField
          label="마케팅 이메일 수신 동의 (선택)"
          checked={marketingEmail}
          onChange={(event) => setMarketingEmail(event.target.checked)}
        />
        <CheckboxField
          label="마케팅 SMS 수신 동의 (선택)"
          checked={marketingSms}
          onChange={(event) => setMarketingSms(event.target.checked)}
        />
      </fieldset>

      {formError ? (
        <p role="alert" className="text-sm text-destructive">
          {formError}
        </p>
      ) : null}

      <Button type="submit" isLoading={isPending} className={SUBMIT_BUTTON_CLASS}>
        가입하기
        <SubmitArrow />
      </Button>
    </form>
  );
};
